import React from "react";
import { Alert, Box, Divider, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface OvernightCounterProps {
  parentName1: string;
  parentName2: string;
  count1: number;
  count2: number;
  total: number;
  totalDaysInRange: number;
  yearRange: 1 | 2;
}

const OvernightCounter: React.FC<OvernightCounterProps> = ({
  parentName1,
  parentName2,
  count1,
  count2,
  total,
  totalDaysInRange,
  yearRange,
}) => {
  const { t } = useTranslation();
  const remaining = totalDaysInRange - total;

  // Days / yearRange calculation for each parent (used in GA child support formula)
  const parent1DaysOver2 = (count1 / yearRange).toFixed(2);
  const parent2DaysOver2 = (count2 / yearRange).toFixed(2);

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 3, p: 2, borderColor: "#dee2e6" }}
      role="region"
      aria-label={t("calendar.overnightCounterTitle")}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1.5 }}
        id="overnight-counter-heading"
      >
        {t("calendar.overnightCounterTitle")}
      </Typography>

      {/* 
        WCAG 4.1.3 Status Messages:
        aria-live="polite" ensures screen readers announce count changes
        without interrupting the user's current activity.
      */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        aria-labelledby="overnight-counter-heading"
      >
        {/* Raw day counts */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            mb: 1.5,
            alignItems: "baseline",
          }}
        >
          <Typography variant="body2">
            <strong>{parentName1}:</strong>{" "}
            <span aria-label={`${parentName1}: ${count1} days`}>
              {count1} {t("calendar.days")}
            </span>
          </Typography>
          <Typography variant="body2">
            <strong>{parentName2}:</strong>{" "}
            <span aria-label={`${parentName2}: ${count2} days`}>
              {count2} {t("calendar.days")}
            </span>
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("calendar.total")}: {total} / {totalDaysInRange}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Days / yearRange output (used for Schedule C calculation) */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
          {yearRange === 2 ? t("calendar.daysOver2Title") : "Days / 1 (Schedule C Value)"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "baseline",
          }}
        >
          <Typography variant="body2">
            <strong>{parentName1}:</strong>{" "}
            <span aria-label={`${parentName1} days divided by 2: ${parent1DaysOver2}`}>
              {parent1DaysOver2}
            </span>
          </Typography>
          <Typography variant="body2">
            <strong>{parentName2}:</strong>{" "}
            <span aria-label={`${parentName2} days divided by 2: ${parent2DaysOver2}`}>
              {parent2DaysOver2}
            </span>
          </Typography>
        </Box>
      </Box>

      {/* Status alerts — role="status" ensures they're announced */}
      {total > totalDaysInRange && (
        <Alert severity="error" sx={{ mt: 2 }} role="alert">
          {t("calendar.exceedsTotalWarning", { total: totalDaysInRange })}
        </Alert>
      )}

      {total < totalDaysInRange && (
        <Alert severity="info" sx={{ mt: 2 }} role="status">
          {t("calendar.remainingDays", { count: remaining })}
        </Alert>
      )}

      {total === totalDaysInRange && (
        <Alert severity="success" sx={{ mt: 2 }} role="status">
          {t("calendar.allDaysAssigned", { total: totalDaysInRange })}
        </Alert>
      )}
    </Paper>
  );
};

export default OvernightCounter;
